import type { ReactElement } from "react";
import { X } from "lucide-react";
import { Combobox } from "../../../../shared/components/ui/combobox";
import { LANGUAGE_OPTIONS } from "../../../../utils/languageMapping";

interface LanguageMultiSelectProps {
  readonly value: string[];
  readonly onChange: (languages: string[]) => void;
  readonly disabled?: boolean;
  readonly containerClassName?: string;
  readonly badgeClassName?: string;
  readonly comboboxTriggerClassName?: string;
  readonly searchInputClassName?: string;
}

type Language = (typeof LANGUAGE_OPTIONS)[number];

/**
 * Multi-select component for title languages
 * Shows selected languages as badges with remove buttons
 * Can be used in both inline table editing and full form contexts
 */
export function LanguageMultiSelect({
  value,
  onChange,
  disabled,
  containerClassName = "flex items-center justify-between gap-2 w-full",
  comboboxTriggerClassName = "w-6 h-6 p-0 border-none",
  searchInputClassName = "h-9 pt-4",
}: Readonly<LanguageMultiSelectProps>): ReactElement {
  const handleAdd = (code: string | null) => {
    if (code && !value.includes(code)) {
      onChange([...value, code]);
    }
  };

  const handleRemove = (code: string) => {
    onChange(value.filter((lang) => lang !== code));
  };

  const availableLanguages = LANGUAGE_OPTIONS.filter((lang) => !value.includes(lang.value));
  const selectedLanguages = value
    .map((code) => LANGUAGE_OPTIONS.find((lang) => lang.value === code))
    .filter((lang): lang is Language => lang !== undefined);

  return (
    <div className={`${containerClassName} relative`}>
      {/* Selected language badges */}
      <div className="flex items-center gap-1 flex-wrap flex-1">
        {selectedLanguages.length === 0 && <span className="text-xs text-neutral-500 italic">add language</span>}
        {selectedLanguages.map(({ value: code, label: name }) => (
          <div key={code} className="text-xs text-lumi-accent items-center flex gap-1 px-1 py-1.5">
            <span>{name}</span>
            {!disabled && (
              <button type="button" onClick={() => handleRemove(code)} className="text-neutral-400 hover:text-white">
                <X className="h-3 w-3" />
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Add language combobox - wrapper positioned relative for absolute dropdown */}
      {!disabled && availableLanguages.length > 0 && (
        <Combobox
          value={null}
          onValueChange={handleAdd}
          options={availableLanguages}
          placeholder="+"
          searchPlaceholder="Search"
          emptyMessage="No language found."
          disabled={disabled}
          fullWidthDropdown={true}
          triggerClassName={comboboxTriggerClassName}
          buttonClassName="hover:bg-transparent"
          textClassName="text-base font-normal"
          iconClassName="hidden"
          dropdownClassName="text-xs mt-1 h-[200px] rounded-lg"
          itemClassName="text-xs py-1.5"
          searchInputClassName={searchInputClassName}
        />
      )}
    </div>
  );
}
